import { useCallback, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import { Box, Button, Grid, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { APIEndpoint } from 'common/enum';
import { addressFormInterface } from 'common/interface';
import { universalPutRequest } from 'common/requestHandler';
import { popupNotification } from 'notifications/ToastNotification';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import colors from 'theme/colors';

import InputElement from '../elements/InputElement';
import { validatePersonalDetails } from './validation';

// SecondaryButton styled component matching the one in PreferenceAdmin
const SecondaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  color: colors.primary,
  border: `2px solid rgba(18, 112, 220, 0.2)`,
  borderRadius: 12,
  textTransform: 'none',
  fontWeight: 600,
  padding: '10px 20px',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor: 'rgba(18, 112, 220, 0.1)',
    borderColor: colors.primary,
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 15px rgba(18, 112, 220, 0.2)`,
  },
}));

const AddressUpdateComp: React.FC<{
  onAddressChange?: (data: addressFormInterface) => void;
  handleSubmitForm?: (data: addressFormInterface) => void;
  addressDefaultValues?: addressFormInterface;
  showCompanyName?: boolean;
  showEditIcon?: boolean;
  submitButtonLabel?: string;
  shouldReset?: boolean;
}> = ({
  onAddressChange,
  handleSubmitForm,
  addressDefaultValues = {},
  showCompanyName = false,
  showEditIcon = true,
  submitButtonLabel = 'Update',
  shouldReset = false,
}) => {
  const { username } = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(false);
  const [isEditable, setIsEditable] = useState(false);

  const {
    control,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<addressFormInterface>({
    resolver: yupResolver(validatePersonalDetails(showCompanyName)),
    mode: 'onChange',
    defaultValues: addressDefaultValues,
  });

  // Reset form when default values change
  useEffect(() => {
    if (addressDefaultValues) {
      reset(addressDefaultValues); // Reset form when default values change
    }
  }, [JSON.stringify(addressDefaultValues), reset]);
  // Submit logic
  const defaultSubmitHandler = useCallback(
    async (data: addressFormInterface) => {
      setLoading(true);
      const addressObject = {
        address1: data.address1,
        address2: data.address2,
        address3: data.address3,
        city: data.city,
        state: data.state,
        postCode: data.postCode,
      };

      const response = await universalPutRequest(
        APIEndpoint.updateUserDetails,
        {
          username,
          address: JSON.stringify(addressObject),
          actionName: 'user',
        }
      );

      if ('error' in response) {
        popupNotification(response.error, false);
      } else {
        popupNotification(response.message, true);
      }
      setLoading(false);
    },
    [username]
  );

  const submitHandler = useCallback(
    (data: addressFormInterface) => {
      if (handleSubmitForm) {
        handleSubmitForm(data);
        reset({
          companyName: '',
          address1: '',
          address2: '',
          address3: '',
          city: '',
          state: '',
          postCode: '',
        });
      } else {
        defaultSubmitHandler(data);
      }
    },
    [handleSubmitForm, defaultSubmitHandler]
  );

  // Input Fields Configuration
  const inputFields = [
    { name: 'address1', label: 'Address 1' },
    { name: 'address2', label: 'Address 2' },
    { name: 'address3', label: 'Address 3' },
    { name: 'city', label: 'City' },
    { name: 'state', label: 'State' },
    { name: 'postCode', label: 'Postal Code' },
  ];

  return (
    <Box>
      <Box
      // sx={{ boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.1)' }}
      // p={5}
      // pt={2}
      // borderRadius={8}
      >
        {showEditIcon && (
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Tooltip title="Edit" placement="top" arrow>
              <IconButton onClick={() => setIsEditable((prev) => !prev)}>
                <ModeEditIcon sx={{ color: colors.editIcon }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <form onSubmit={handleSubmit(submitHandler)}>
          <Grid container spacing={2}>
            {showCompanyName && (
              <Grid item xs={12}>
                <InputElement
                  name="companyName"
                  control={control}
                  label="Company Name"
                  disabled={isEditable}
                />
              </Grid>
            )}

            {inputFields.map((field) => (
              <Grid key={field.name} item xs={12} md={6} sx={{ paddingTop: 0 }}>
                <InputElement
                  name={field.name}
                  control={control}
                  label={field.label}
                  disabled={isEditable}
                />
              </Grid>
            ))}

            <Grid item xs={12}>
              <Box textAlign="right" mt={4}>
                <SecondaryButton type="submit" disabled={isEditable || loading}>
                  {submitButtonLabel}
                </SecondaryButton>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Box>
  );
};

export default AddressUpdateComp;
